import { useState } from "react";
import { ChevronLeft, ChevronRight, Facebook, Instagram } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

const DAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
const DOT = { pending: "#D97706", posting: "#3B82F6", posted: "#059669", sent: "#059669", failed: "#E11D48", cancelled: "#94A3B8" };
const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const isMovable = (p, canEdit) => canEdit && p.status === "pending" && new Date(p.send_at) > new Date();

const CalendarChip = ({ p, canEdit, onDragStart, onClick }) => {
  const movable = isMovable(p, canEdit);
  const t = new Date(p.send_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
  return (
    <div
      draggable={movable}
      onDragStart={(e) => { e.dataTransfer.setData("text/plain", p.id); onDragStart(p.id); }}
      onClick={() => movable && onClick(p)}
      title={`${p.title || p.text.slice(0, 60)}${movable ? " — seret untuk pindah tanggal" : ""}`}
      data-testid={`calendar-chip-${p.id}`}
      className={`flex items-center gap-1 text-[10px] px-1.5 py-1 rounded-md border bg-[#FFFFFF] truncate transition-shadow ${movable ? "cursor-grab active:cursor-grabbing hover:shadow-md" : "opacity-70"}`}
      style={{ borderLeft: `3px solid ${DOT[p.status] || DOT.pending}` }}
    >
      <span className="font-mono-code text-slate-500 shrink-0">{t}</span>
      {(p.auto_social || []).includes("facebook") && <Facebook size={10} className="text-[#1877F2] shrink-0" />}
      {(p.auto_social || []).includes("instagram") && <Instagram size={10} className="text-[#E1306C] shrink-0" />}
      <span className="truncate text-slate-700">{p.title || p.text}</span>
    </div>
  );
};

export default function ContentCalendar({ posts, canEdit, onChanged, onEdit }) {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [dragId, setDragId] = useState(null);
  const [overKey, setOverKey] = useState("");

  const byDay = {};
  posts.forEach((p) => { const k = dayKey(new Date(p.send_at)); (byDay[k] = byDay[k] || []).push(p); });
  Object.values(byDay).forEach((l) => l.sort((a, b) => a.send_at.localeCompare(b.send_at)));

  const gridStart = new Date(month);
  gridStart.setDate(1 - ((month.getDay() + 6) % 7));
  const cells = Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
  const todayKey = dayKey(new Date());

  const drop = async (day) => {
    setOverKey("");
    const p = posts.find((x) => x.id === dragId);
    setDragId(null);
    if (!p || dayKey(new Date(p.send_at)) === dayKey(day)) return;
    const old = new Date(p.send_at);
    const next = new Date(day.getFullYear(), day.getMonth(), day.getDate(), old.getHours(), old.getMinutes());
    if (next <= new Date()) { toast.error("Tidak bisa dipindah ke waktu yang sudah lewat"); return; }
    try {
      await api.put(`/scheduled-posts/${p.id}`, { send_at: next.toISOString() });
      toast.success(`"${p.title || "Konten"}" dipindah ke ${next.toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}`);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));

  return (
    <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4" data-testid="content-calendar">
      <div className="flex items-center gap-2 mb-3">
        <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="calendar-prev" className="h-8 w-8 p-0 rounded-lg"><ChevronLeft size={15} /></Button>
        <p className="font-heading font-bold text-sm capitalize min-w-[140px] text-center" data-testid="calendar-month-label">{month.toLocaleString("id-ID", { month: "long", year: "numeric" })}</p>
        <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="calendar-next" className="h-8 w-8 p-0 rounded-lg"><ChevronRight size={15} /></Button>
        <Button variant="ghost" size="sm" onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }} data-testid="calendar-today" className="h-8 text-xs">Hari ini</Button>
        <div className="flex-1" />
        <div className="hidden md:flex gap-3 text-[10px] text-slate-500">
          {[["pending", "Terjadwal"], ["posted", "Terposting"], ["failed", "Gagal"], ["cancelled", "Dibatalkan"]].map(([k, l]) => (
            <span key={k} className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: DOT[k] }} />{l}</span>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-slate-500 mb-1">
        {DAYS.map((d) => <div key={d} className="px-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d) => {
          const k = dayKey(d);
          const list = byDay[k] || [];
          const inMonth = d.getMonth() === month.getMonth();
          return (
            <div
              key={k}
              onDragOver={(e) => { if (dragId) { e.preventDefault(); setOverKey(k); } }}
              onDragLeave={() => setOverKey((o) => (o === k ? "" : o))}
              onDrop={(e) => { e.preventDefault(); drop(d); }}
              data-testid={`calendar-day-${k}`}
              className={`min-h-[96px] rounded-lg border p-1 space-y-1 transition-colors ${overKey === k ? "border-[#2563EB] bg-[#EFF6FF]" : "border-[#E2E8F0]"} ${inMonth ? "bg-[#FFFFFF]" : "bg-[#FFFFFF]"}`}
            >
              <div className={`text-[11px] font-medium px-1 ${k === todayKey ? "text-white bg-[#2563EB] rounded-full w-5 h-5 flex items-center justify-center" : inMonth ? "text-slate-700" : "text-slate-600"}`}>{d.getDate()}</div>
              <div className="space-y-1 max-h-[92px] overflow-y-auto" data-testid={`calendar-list-${k}`}>
                {list.map((p) => <CalendarChip key={p.id} p={p} canEdit={canEdit} onDragStart={setDragId} onClick={onEdit} />)}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-500 mt-2">Seret konten berstatus Terjadwal ke tanggal lain untuk memindahkan jadwal (jam tetap sama). Klik untuk ubah detail.</p>
    </div>
  );
}
